/* global axios */
import ApiClient from './ApiClient';
import {
  buildSummaryReportParams,
  pickScopeFilters,
} from './buildSummaryReportParams';
import { downloadStatisticsCsvAsync } from 'dashboard/helper/downloadHelper';

const UTC_TIMEZONE_OFFSET = 0;

class ReportsAPI extends ApiClient {
  constructor() {
    super('reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReports({
    metric,
    from,
    to,
    type = 'account',
    id,
    groupBy,
    businessHours,
    timezoneOffset,
    labelIds,
    excludeLabelIds,
    inboxIds,
    teamIds,
    agentIds,
    channelTypes,
    tagPresence,
    resolutionMethod,
    statusAtPeriodEnd,
    reopening,
    transfers,
    humanResponse,
  }) {
    const params = {
      metric,
      since: from,
      until: to,
      type,
      id,
      group_by: groupBy,
      business_hours: businessHours,
      timezone_offset:
        timezoneOffset === undefined || timezoneOffset === null
          ? UTC_TIMEZONE_OFFSET
          : timezoneOffset,
      ...buildSummaryReportParams(
        pickScopeFilters({
          labelIds,
          excludeLabelIds,
          inboxIds,
          teamIds,
          agentIds,
          channelTypes,
          tagPresence,
          resolutionMethod,
          statusAtPeriodEnd,
          reopening,
          transfers,
          humanResponse,
        })
      ),
    };
    return axios.get(`${this.url}`, { params });
  }

  getSummary({
    from,
    to,
    since,
    until,
    type,
    id,
    groupBy,
    businessHours,
    labelIds,
    excludeLabelIds,
    inboxIds,
    teamIds,
    agentIds,
    channelTypes,
    tagPresence,
    resolutionMethod,
    statusAtPeriodEnd,
    reopening,
    transfers,
    humanResponse,
    comparisonPeriods,
  } = {}) {
    const params = {
      since: since ?? from,
      until: until ?? to,
      type: type ?? 'account',
      id,
      group_by: groupBy,
      business_hours: businessHours,
      timezone_offset: UTC_TIMEZONE_OFFSET,
      ...buildSummaryReportParams(
        pickScopeFilters({
          labelIds,
          excludeLabelIds,
          inboxIds,
          teamIds,
          agentIds,
          channelTypes,
          tagPresence,
          resolutionMethod,
          statusAtPeriodEnd,
          reopening,
          transfers,
          humanResponse,
        })
      ),
    };
    if (comparisonPeriods?.length) {
      params.comparison_periods = JSON.stringify(
        comparisonPeriods.map(p => ({ since: p.from, until: p.to }))
      );
    }
    return axios.get(`${this.url}/summary`, { params });
  }

  getConversationMetric(type = 'account', page = 1) {
    return axios.get(`${this.url}/conversations`, {
      params: {
        type,
        page,
      },
    });
  }

  getAgentReports(reportParams = {}) {
    return axios.get(`${this.url}/agents`, {
      params: buildSummaryReportParams(reportParams),
    });
  }

  getConversationsSummaryReports({ from: since, to: until, businessHours }) {
    return axios.get(`${this.url}/conversations_summary`, {
      params: { since, until, business_hours: businessHours },
    });
  }

  getConversationTrafficCSV({ daysBefore = 6 } = {}) {
    return axios.get(`${this.url}/conversation_traffic`, {
      params: { timezone_offset: UTC_TIMEZONE_OFFSET, days_before: daysBefore },
    });
  }

  getLabelReports(reportParams = {}) {
    return axios.get(`${this.url}/labels`, {
      params: buildSummaryReportParams(reportParams),
    });
  }

  getInboxReports(reportParams = {}) {
    return axios.get(`${this.url}/inboxes`, {
      params: buildSummaryReportParams(reportParams),
    });
  }

  getQueuedCustomers(reportParams = {}) {
    const params = buildSummaryReportParams(reportParams);
    if (reportParams.result && reportParams.result !== 'all') {
      params.result = reportParams.result;
    }
    if (reportParams.entryType && reportParams.entryType !== 'all') {
      params.entry_type = reportParams.entryType;
    }
    if (reportParams.summaryGroupBy) {
      params.summary_group_by = reportParams.summaryGroupBy;
    }
    if (reportParams.comparisonPeriods?.length) {
      params.comparison_periods = JSON.stringify(
        reportParams.comparisonPeriods.map(p => ({
          since: p.from ?? p.since,
          until: p.to ?? p.until,
        }))
      );
    }
    return axios.get(`${this.url}/queued_customers`, { params });
  }

  getQueuedCustomersCsv(reportParams = {}) {
    const params = buildSummaryReportParams(reportParams);
    if (reportParams.result && reportParams.result !== 'all') {
      params.result = reportParams.result;
    }
    if (reportParams.entryType && reportParams.entryType !== 'all') {
      params.entry_type = reportParams.entryType;
    }
    return axios.get(`${this.url}/queued_customers_csv`, { params });
  }

  getTeamReports(reportParams = {}) {
    return axios.get(`${this.url}/teams`, {
      params: buildSummaryReportParams(reportParams),
    });
  }

  getOverviewReports(reportParams = {}) {
    return axios.get(`${this.url}/overview`, {
      params: buildSummaryReportParams(reportParams),
    });
  }

  getChatsCsv(reportParams = {}) {
    return axios.get(`${this.url}/chats`, {
      params: buildSummaryReportParams(reportParams),
    });
  }

  getBotMetrics({ from, to } = {}) {
    return axios.get(`${this.url}/bot_metrics`, {
      params: { since: from, until: to },
    });
  }

  getBotPage(reportParams = {}) {
    const params = buildSummaryReportParams(reportParams);
    if (reportParams.botOutcome && reportParams.botOutcome !== 'all') {
      params.bot_outcome = reportParams.botOutcome;
    }
    if (reportParams.summaryGroupBy) {
      params.summary_group_by = reportParams.summaryGroupBy;
    }
    if (reportParams.comparisonPeriods?.length) {
      params.comparison_periods = JSON.stringify(
        reportParams.comparisonPeriods.map(p => ({
          since: p.from ?? p.since,
          until: p.to ?? p.until,
        }))
      );
    }
    return axios.get(`${this.url}/bot`, { params });
  }

  getBotCsv(reportParams = {}) {
    const params = buildSummaryReportParams(reportParams);
    if (reportParams.botOutcome && reportParams.botOutcome !== 'all') {
      params.bot_outcome = reportParams.botOutcome;
    }
    return axios.get(`${this.url}/bot_csv`, { params });
  }

  getBotSummary({ from, to, groupBy, businessHours, comparisonPeriods } = {}) {
    const params = {
      since: from,
      until: to,
      type: 'account',
      group_by: groupBy,
      business_hours: businessHours,
      timezone_offset: UTC_TIMEZONE_OFFSET,
    };
    if (comparisonPeriods?.length) {
      params.comparison_periods = JSON.stringify(
        comparisonPeriods.map(p => ({ since: p.from, until: p.to }))
      );
    }
    return axios.get(`${this.url}/bot_summary`, { params });
  }

  getAgentActivity({
    from: since,
    to: until,
    userId,
    agentIds,
    teamIds,
    comparisonPeriods,
    exportCsv = false,
  }) {
    const params = { since, until };
    if (userId) params.user_id = userId;
    if (agentIds?.length) params.agent_ids = agentIds;
    if (teamIds?.length) params.team_ids = teamIds;
    if (comparisonPeriods?.length) {
      params.comparison_periods = JSON.stringify(
        comparisonPeriods.map(period => ({
          since: period.from ?? period.since,
          until: period.to ?? period.until,
        }))
      );
    }
    if (exportCsv) params.export = 'csv';
    return axios.get(`${this.url}/agent_activity`, { params });
  }

  getSummaryCharts({
    type,
    labelId,
    agentId,
    teamId,
    inboxId,
    ...reportParams
  } = {}) {
    return axios.get(`${this.url}/summary_charts`, {
      params: {
        type,
        ...(labelId ? { label_id: labelId } : {}),
        ...(agentId ? { agent_id: agentId } : {}),
        ...(teamId ? { team_id: teamId } : {}),
        ...(inboxId ? { inbox_id: inboxId } : {}),
        ...buildSummaryReportParams(reportParams),
      },
    });
  }

  createArchiveIntent(intent) {
    return axios.post(`${this.url}/archive_intent`, intent);
  }

  resolveArchiveIntent(token) {
    return axios.get(`${this.url}/archive_resolve`, { params: { token } });
  }

  startCsvExport(section, reportParams = {}) {
    return axios.post(`${this.url}/csv_exports`, {
      section,
      ...buildSummaryReportParams(reportParams),
      ...(reportParams.user_ids ? { user_ids: reportParams.user_ids } : {}),
      ...(reportParams.inbox_id ? { inbox_id: reportParams.inbox_id } : {}),
      ...(reportParams.team_id ? { team_id: reportParams.team_id } : {}),
      ...(reportParams.rating ? { rating: reportParams.rating } : {}),
      ...(reportParams.label_title
        ? { label_title: reportParams.label_title }
        : {}),
      ...(reportParams.exclude_label_title
        ? { exclude_label_title: reportParams.exclude_label_title }
        : {}),
      ...(reportParams.has_comment
        ? { has_comment: reportParams.has_comment }
        : {}),
      ...(reportParams.botOutcome && reportParams.botOutcome !== 'all'
        ? { bot_outcome: reportParams.botOutcome }
        : {}),
      ...(reportParams.result && reportParams.result !== 'all'
        ? { result: reportParams.result }
        : {}),
      ...(reportParams.entryType && reportParams.entryType !== 'all'
        ? { entry_type: reportParams.entryType }
        : {}),
      ...(reportParams.summaryGroupBy
        ? { summary_group_by: reportParams.summaryGroupBy }
        : {}),
      ...(reportParams.userId ? { user_id: reportParams.userId } : {}),
    });
  }

  getCsvExport(id) {
    return axios.get(`${this.url}/csv_exports/${id}`, {
      responseType: 'blob',
      validateStatus: status =>
        status === 200 || status === 202 || status === 422,
    });
  }

  downloadSectionCsv(section, reportParams, fileName) {
    return downloadStatisticsCsvAsync({
      fileName,
      createExport: () => this.startCsvExport(section, reportParams),
      getExport: id => this.getCsvExport(id),
    });
  }
}

export default new ReportsAPI();
